import React, {
	KeyboardEvent as ReactKeyboardEvent,
	useEffect,
	useRef,
} from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { ITheme } from '../../themer';
import themed from '../../themer/themed';

import { DropdownItem, IDropdownItem } from './DropdownItem';
import { DropdownOption } from './DropdownOption';
import { IGetItemDisplayValue, IGetItemKey } from './helpers';
import { IGetSectionHeaderDisplayValue } from './helpers/IGetSectionHeaderDisplayValue';

interface IStyledDropdownListWrapProps {
	isOpen?: boolean;
}

export const StyledDropdownListWrap = styled.div<
	IStyledDropdownListWrapProps,
	ITheme
>`
	position: relative;
	display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

export const StyledDropdownList = styled.div<{}, ITheme>`
	position: absolute;
	width: 100%;
	overflow-y: auto;
	max-height: ${themed(({ dropdownList }) => dropdownList.options.maxHeight)};
	z-index: ${themed(({ dropdownList }) => dropdownList.options.zIndex)};
	margin-top: ${themed(({ dropdownList }) => dropdownList.options.marginTop)};
	box-shadow: ${themed(({ dropdownList }) => dropdownList.options.boxShadow)};
	background: ${themed(({ dropdownList }) => dropdownList.options.background)};
`;

interface IDropdownListProps {
	getMenuProps: PropGetters<IDropdownItem>['getMenuProps'];
	getItemProps: PropGetters<IDropdownItem>['getItemProps'];
	setHighlightedIndex: (index: number) => void;
	selectItem: (item: IDropdownItem) => void;
	closeMenu: () => void;
	getItemDisplayValue: IGetItemDisplayValue;
	getSectionHeaderDisplayValue?: IGetSectionHeaderDisplayValue;
	selectedItem: IDropdownItem | null;
	highlightedIndex: number | null;
	getItemKey: IGetItemKey;
	items: IDropdownItem[];
	isOpen: boolean;
	defaultScrollToNthItem?: number;
	/**
	 * Opt in to real DOM focus management: on open the highlighted option
	 * receives focus and a listbox-level key handler drives navigation.
	 * Use for select-style triggers (Select). Leave off for combobox-with-
	 * input patterns (AutoSuggest) where focus must stay on the input.
	 */
	manageFocus?: boolean;
}

export const DropdownList = ({
	getItemDisplayValue,
	getSectionHeaderDisplayValue,
	highlightedIndex,
	selectedItem,
	getMenuProps,
	getItemProps,
	setHighlightedIndex,
	selectItem,
	closeMenu,
	getItemKey,
	isOpen,
	items,
	defaultScrollToNthItem,
	manageFocus,
}: IDropdownListProps) => {
	const listRef = useRef<HTMLDivElement>(null);
	const optionRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
	const wasOpenRef = useRef(false);

	// defaultScrollToNthItem will automatically scroll to the item by default
	useEffect(() => {
		if (
			defaultScrollToNthItem &&
			listRef.current &&
			optionRef.current &&
			isOpen
		) {
			// eslint-disable-next-line no-param-reassign
			listRef.current.scrollTop = optionRef.current.offsetTop;
		}
	}, [isOpen, defaultScrollToNthItem]);

	const selectableItems = items.filter(
		(item) => typeof item === 'string' || item.type !== 'header',
	);

	// On open, move real DOM focus onto the highlighted option so Android
	// TalkBack announces it. (TalkBack announces the focused element; it
	// does not follow aria-activedescendant.)
	useEffect(() => {
		if (!manageFocus) return;
		if (isOpen && !wasOpenRef.current) {
			const idx =
				highlightedIndex != null && highlightedIndex >= 0
					? highlightedIndex
					: 0;
			optionRefs.current[idx]?.focus();
		}
		wasOpenRef.current = isOpen;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, manageFocus]);

	// Keep DOM focus in sync with highlightedIndex while navigating with the
	// keyboard. Gated on focus already being inside the listbox so mouse hover
	// doesn't steal focus from elsewhere.
	useEffect(() => {
		if (!manageFocus) return;
		if (!isOpen) return;
		if (highlightedIndex == null || highlightedIndex < 0) return;
		const list = listRef.current;
		const active = document.activeElement;
		if (list && (list === active || list.contains(active))) {
			optionRefs.current[highlightedIndex]?.focus();
		}
	}, [highlightedIndex, isOpen, manageFocus]);

	const handleListKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
		const count = selectableItems.length;
		if (count === 0) return;
		const current = highlightedIndex ?? -1;

		switch (e.key) {
			case 'ArrowDown': {
				e.preventDefault();
				setHighlightedIndex(current < count - 1 ? current + 1 : 0);
				break;
			}
			case 'ArrowUp': {
				e.preventDefault();
				setHighlightedIndex(current > 0 ? current - 1 : count - 1);
				break;
			}
			case 'Enter':
			case ' ': {
				e.preventDefault();
				if (current >= 0 && current < count) {
					selectItem(selectableItems[current]);
				}
				closeMenu();
				break;
			}
			case 'Escape': {
				e.preventDefault();
				closeMenu();
				break;
			}

			default:
		}
	};

	let selectableIndex = 0;
	optionRefs.current = [];

	return (
		<StyledDropdownListWrap isOpen={isOpen}>
			<StyledDropdownList
				{...getMenuProps({ ref: listRef })}
				data-testid="list"
				tabIndex={-1}
				onKeyDown={manageFocus ? handleListKeyDown : undefined}
			>
				{isOpen &&
					items.map((item: IDropdownItem) => {
						if (typeof item !== 'string' && item.type === 'header') {
							if (getSectionHeaderDisplayValue) {
								return getSectionHeaderDisplayValue(item);
							}

							return <div>{item.title}</div>;
						}

						const index = selectableIndex;
						selectableIndex += 1;

						return (
							<DropdownOption
								key={getItemKey(item)}
								ref={(el: HTMLDivElement | null) => {
									optionRefs.current[index] = el;
									if (index === defaultScrollToNthItem) {
										(
											optionRef as React.MutableRefObject<HTMLDivElement | null>
										).current = el;
									}
								}}
								tabIndex={manageFocus ? -1 : undefined}
								hasSecondaryStyle={
									typeof item !== 'string' && item.hasSecondaryStyle
								}
								isHighlighted={highlightedIndex === index}
								{...getItemProps({
									index,
									item,
									isSelected: selectedItem === item,
								})}
							>
								<DropdownItem
									displayValue={getItemDisplayValue(item)}
									hint={typeof item !== 'string' ? item.hint : undefined}
								/>
							</DropdownOption>
						);
					})}
			</StyledDropdownList>
		</StyledDropdownListWrap>
	);
};
